import { Injectable, OnDestroy } from '@angular/core';
import { Subject, Subscription } from 'rxjs';
import { FunctionsHttpError } from '@supabase/supabase-js';
import { HttpErrorResponse } from '@angular/common/http';

import { ToastService } from '#services/toast/toast.service';
import { ErrorMessageDetails, FriendlyErrorMessage } from '#models/error/error-message.model';
import { CustomError } from '#lib/custom-error';
import { logger } from '#lib/logger';

const IGNORED_ERRORS = [
  'User cancelled photos app',
  'User cancelled camera app',
  'pickFiles canceled.',
  'Share canceled',
];

@Injectable({
  providedIn: 'root',
})
export class ErrorService implements OnDestroy {
  errorInstance$ = new Subject<unknown>();
  /**
   * Emits the status code of the error. Used to handle unauthorized errors.
   */
  errorStatus$ = new Subject<number | null>();

  private isErrorDisplayed = false;
  private subscription = new Subscription();

  constructor(private toastService: ToastService) {
    this.subscription.add(this.errorInstance$Subscriber());
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  private errorInstance$Subscriber() {
    return this.errorInstance$.subscribe(async (error) => {
      if (error && !this.isErrorDisplayed) {
        const details = this.getFriendlyErrorMessage(error);

        await this.presentErrorToast({ ...details });
      }
    });
  }

  async presentErrorToast(options: ErrorMessageDetails) {
    this.isErrorDisplayed = true;

    // Promise will resolve when the toast is dismissed.
    await this.toastService.showToast({
      header: options.header ?? '',
      message: options.message,
      duration: 5000,
      color: 'danger',
    });

    // Reset the error state.
    this.isErrorDisplayed = false;
    this.setErrorInstance(null);
  }

  /**
   * Generates a user-friendly error message based on the provided error.
   *
   * @param error - The error object or message to be processed.
   * @param options - Optional settings for the error message.
   * @param options.defaultMessage - A default message to use if no specific message is derived from the error.
   * @returns An object containing the header and the friendly error message.
   */
  getFriendlyErrorMessage(
    error: CustomError | unknown,
    options?: {
      defaultMessage: string;
    }
  ): ErrorMessageDetails {
    let message = options?.defaultMessage ?? FriendlyErrorMessage.DefaultError;
    let header = '';

    if (error && typeof error === 'object' && 'status' in error && error.status === 0) {
      header = 'Network Connectivity Issue';
      message = FriendlyErrorMessage.NetworkError;

      return {
        header,
        message,
      };
    }

    if (error instanceof Error) {
      message = error.message;
    }

    if (typeof error === 'string') {
      message = error;
    }

    return {
      header,
      message,
    };
  }

  /**
   * Sets the error instance and emits it through the errorInstance$ observable.
   *
   * @param error - The error to be set. It can be of any type.
   *
   * Sets the following:
   * * {@link errorInstance$} - The error instance.
   * * {@link errorStatus$} - The status code of the error. If the error is an `HttpErrorResponse` and the status is 401, it emits `401` through the observable.
   *
   * If the error is:
   * - `null`, it does nothing.
   * - A `string`, it creates a `CustomError` with the string as the message and emits it.
   * - An `object` with a `message` property, it creates a `CustomError` with the message, status, and code from the object and emits it.
   * - Any other type, it emits the error as is.
   */
  async setErrorInstance(error: unknown): Promise<void> {
    if (!error) {
      return;
    }

    let customError: CustomError | undefined;

    // Handle string errors
    if (typeof error === 'string') {
      customError = new CustomError(error);

      this.errorInstance$.next(customError);
      logger.log(error, 'error -> string');

      return;
    }

    // Handle Supabase Functions errors
    if (error instanceof FunctionsHttpError) {
      const functionsError = await error.context.json();

      logger.log(functionsError, 'error -> supabase.FunctionsHttpError');

      customError = new CustomError(functionsError.message, functionsError.status);
      this.errorInstance$.next(customError);

      return;
    }

    // Handle HttpErrorResponse
    if (error instanceof HttpErrorResponse) {
      const { message, status, statusText } = error.error;

      // Unknown error (network)
      if (error.status === 0) {
        customError = new CustomError(FriendlyErrorMessage.UnknownError);

        logger.log(error, 'error -> HttpErrorResponse -> status -> Unknown');
        this.errorInstance$.next(customError);

        return;
      }

      // Unauthorized
      if (error.status === 401) {
        customError = new CustomError(FriendlyErrorMessage.UnauthorizedError);
        this.errorStatus$.next(error.status);

        logger.log(error, 'error -> HttpErrorResponse -> status -> Unauthorized');
        this.errorInstance$.next(customError);

        return;
      }

      // Forbidden
      if (error.status === 403) {
        customError = new CustomError(FriendlyErrorMessage.ForbiddenError ?? 'Access forbidden');
        this.errorStatus$.next(error.status);

        logger.log(error, 'error -> HttpErrorResponse -> status -> Forbidden');
        this.errorInstance$.next(customError);

        return;
      }

      // Ignore specific error. This happens if a user logs into the app with another user's account.
      if (error.status === 500 && message.includes('user_fcm_tokens_fcm_token_key')) {
        logger.log(error, 'error -> HttpErrorResponse -> status -> 500 (ignore)');
        return;
      }

      // Other HttpErrorResponse
      customError = new CustomError(
        message === undefined ? error.message : message,
        status,
        statusText
      );

      logger.log(error, 'error -> HttpErrorResponse');
      this.errorInstance$.next(customError);

      return;
    }

    // Handle generic object errors with a message property
    if (typeof error === 'object' && error !== null && 'message' in error) {
      let { message } = error as { message: string };

      // If the message is empty, use a default message.
      if (!message) {
        message = FriendlyErrorMessage.OperationCanceled;
      } else if (IGNORED_ERRORS.some((error) => message.includes(error))) {
        // Ignore user cancelled errors.
        return;
      }

      customError = new CustomError(message);
      this.errorInstance$.next(customError);
      logger.log(error, 'error -> object with message property');

      return;
    }

    // Fallback: emit the error as is
    this.errorInstance$.next(error);
  }

  setErrorStatus(status: number | null) {
    this.errorStatus$.next(status);
  }
}
